import React from 'react';
import { Accordion, Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { ApplicationResourcesDataService } from '../../Services/ApplicationResourcesDataService';
import { buildNavigationResourceTree } from '../../Navigation/navigationConfig';
import { useSecurity } from '../../Contexts/SecurityContext';
import './PermissionEditorModal.scss';

const activeIds = node => [...(node.isAssignable && node.isActive ? [node.resourceId] : []), ...node.children.flatMap(activeIds)];
const groupIds = nodes => nodes.flatMap(node => [String(node.resourceId), ...groupIds(node.children)]);
const level = execute => execute ? 'View + Execute' : 'View';
const errorMessage = error => {
    const problem = error?.response?.data;
    return Object.values(problem?.errors || {}).flat()[0] || problem?.detail || problem?.title || error.message || 'Request failed.';
};
const payload = grants => [...grants].sort(([a], [b]) => a - b).map(([resourceId, canExecute]) => ({ resourceId, canExecute }));
export const GrantSelect = ({ label, value, onChange, disabled, userMode, bulk = false }) => (
    <Form.Select size="sm" aria-label={label} value={value} disabled={disabled} onChange={event => onChange(event.target.value)}>
        {bulk && <option value="mixed">Choose access</option>}
        <option value="none">{userMode ? 'No direct assignment' : 'No Access'}</option>
        <option value="view">View</option><option value="execute">View + Execute</option>
    </Form.Select>
);
const ResourceNode = ({ node, grants, historicalGrants, details, userMode, readOnly, change, depth = 0 }) => {
    const children = node.children.map(child => <ResourceNode key={child.resourceId} {...{ node: child, grants, historicalGrants, details, userMode, readOnly, change, depth: depth + 1 }} />);
    if (!node.isAssignable) {
        const ids = activeIds(node);
        const levels = new Set(ids.map(id => grants.has(id) ? grants.get(id) ? 'execute' : 'view' : 'none'));
        return <Accordion.Item eventKey={String(node.resourceId)}>
            <div className="permission-resource-group-heading">
                <Accordion.Button className='border-0'>{node.name}</Accordion.Button>
                <div className="permission-bulk-control"><GrantSelect label={`Access for group ${node.name}`} bulk userMode={userMode}
                    value={levels.size === 1 ? [...levels][0] : 'mixed'} disabled={readOnly || !ids.length} onChange={value => change(ids, value)} /></div>
            </div>
            <Accordion.Collapse eventKey={String(node.resourceId)}><div>{children}</div></Accordion.Collapse>
        </Accordion.Item>;
    }
    const detail = details.get(node.resourceId);
    const assigned = grants.has(node.resourceId) ? grants.get(node.resourceId) : historicalGrants.get(node.resourceId);
    return <>
        <div className={`permission-access-row ${userMode ? 'permission-access-user' : ''}`} style={{ paddingLeft: `${Math.min(depth, 3) * .5 + .75}rem` }}>
            <div>{node.name}{!node.isActive && <Badge className="ms-2" bg="secondary">Inactive</Badge>}
                {detail?.isDirect && detail?.isInherited && <small className="d-block text-muted">Direct + inherited</small>}
            </div>
            {userMode && <span aria-label={`Inherited access for ${node.name}`}>{detail?.isInherited ? level(detail.inheritedCanExecute) : '—'}</span>}
            <GrantSelect label={`Direct access for ${node.name}`} userMode={userMode}
                value={assigned === undefined ? 'none' : assigned ? 'execute' : 'view'}
                disabled={readOnly || !node.isActive} onChange={value => change([node.resourceId], value)} />
            {userMode && <span aria-label={`Effective access for ${node.name}`}>{detail ? level(detail.canExecute) : '—'}</span>}
        </div>{children}
    </>;
};
const PermissionEditorModal = ({ title, description, loadPermissionSet, savePermissionSet, onHide, onSaved, refreshSecurityAfterSave = false, permission, userMode = false }) => {
    const { acquireAccessToken, refreshSecurity, canExecute } = useSecurity();
    const readOnly = !canExecute(permission);
    const [tree, setTree] = React.useState([]);
    const [companyGrants, setCompanyGrants] = React.useState(new Map());
    const [grants, setGrants] = React.useState(new Map());
    const [original, setOriginal] = React.useState('[]');
    const [details, setDetails] = React.useState(new Map());
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [historical, setHistorical] = React.useState(false);
    const [historicalGrants, setHistoricalGrants] = React.useState(new Map());
    const [unmappedCodes, setUnmappedCodes] = React.useState([]);
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await acquireAccessToken();
                const [resources, result] = await Promise.all([ApplicationResourcesDataService.getResources(token, true), loadPermissionSet(token)]);
                if (cancelled) return;
                const presentation = buildNavigationResourceTree(resources.filter(item => !item.isCompanyScoped));
                const nodes = presentation.unmapped.length ? [...presentation.tree, {
                    resourceId: 'unmapped-navigation-resources', code: '', name: 'Not in application navigation',
                    isActive: true, isAssignable: false, children: presentation.unmapped.map(item => ({ ...item, children: [] }))
                }] : presentation.tree;
                const valid = new Set(nodes.flatMap(activeIds));
                const companyIds = new Set(resources.filter(item => item.isCompanyScoped).map(item => item.resourceId));
                const stored = (result.assignedPermissions || []).filter(item => !companyIds.has(item.resourceId));
                setCompanyGrants(new Map((result.assignedPermissions || []).filter(item => companyIds.has(item.resourceId))
                    .map(item => [item.resourceId, item.canExecute])));
                const assigned = new Map(stored.filter(item => valid.has(item.resourceId)).map(item => [item.resourceId, item.canExecute]));
                setTree(nodes); setGrants(assigned); setOriginal(JSON.stringify(payload(assigned)));
                setHistorical(stored.some(item => !valid.has(item.resourceId)));
                setHistoricalGrants(new Map(stored.filter(item => !valid.has(item.resourceId)).map(item => [item.resourceId, item.canExecute])));
                setDetails(new Map((result.effectivePermissionDetails || []).map(item => [item.resourceId, item])));
                setUnmappedCodes(presentation.unmapped.map(item => item.code));
            } catch (e) { if (!cancelled) setError(errorMessage(e)); }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [acquireAccessToken, loadPermissionSet]);
    const change = (ids, value) => {
        if (readOnly || saving || value === 'mixed') return;
        setGrants(current => {
            const next = new Map(current);
            ids.forEach(id => value === 'none' ? next.delete(id) : next.set(id, value === 'execute'));
            return next;
        });
    };
    const dirty = JSON.stringify(payload(grants)) !== original;
    const save = async () => {
        if (readOnly || saving || loading) return;
        setSaving(true); setError('');
        try {
            await savePermissionSet(payload(new Map([...companyGrants, ...grants])), await acquireAccessToken());
            if (refreshSecurityAfterSave) refreshSecurity();
            onSaved();
        } catch (e) { setError(errorMessage(e)); }
        finally { setSaving(false); }
    };
    return <Modal show size="lg" centered onHide={saving ? undefined : onHide} backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}><Modal.Title>{title}</Modal.Title></Modal.Header>
        <Modal.Body>
            <p className="small text-muted">{description}</p>
            {readOnly && <p className="small text-muted">Read-only access</p>}
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? <Spinner animation="border" /> : <>
                <div className="permission-select-all"><span>Set all active resources</span>
                    <div className="permission-bulk-control"><GrantSelect label="Access for all resources" value="mixed" bulk userMode={userMode}
                        disabled={readOnly || saving} onChange={value => change(tree.flatMap(activeIds), value)} /></div>
                </div>
                <div className="overflow-auto">
                    {userMode && <div className="permission-access-row permission-access-user fw-semibold border-0"><span>Resource</span><span>Inherited</span><span>Direct</span><span>Effective (saved)</span></div>}
                    <Accordion alwaysOpen defaultActiveKey={groupIds(tree)} className="permission-resource-tree">
                        {tree.map(node => <ResourceNode key={node.resourceId} {...{ node, grants, historicalGrants, details, userMode, readOnly: readOnly || saving, change }} />)}
                    </Accordion>
                </div>
                {userMode && dirty && <p className="small text-muted mt-2">Effective access shows the last saved result. Direct grants cannot remove inherited access. Reopen after saving to see the updated result.</p>}
                {historical && <p className="small text-muted mt-2">Inactive historical grants cannot be assigned and will be omitted when saving.</p>}
                {unmappedCodes.length > 0 && <Alert variant="warning" className="small mt-2 mb-0">
                    These assignable resources are not mapped to application navigation: {unmappedCodes.join(', ')}.
                </Alert>}
            </>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" disabled={saving} onClick={onHide}>Close</Button>
            {!readOnly && <Button disabled={loading || saving || (!dirty && !historical)} onClick={save}>{saving ? 'Saving...' : 'Save permissions'}</Button>}
        </Modal.Footer>
    </Modal>;
};
export default PermissionEditorModal;
